/**
 * PII-free feature extraction for document-type classification.
 *
 * The guiding idea: a document's *type* lives in what is invariant across every
 * specimen — the printed template and the physical form factor — not in the
 * person's data. So we extract signals that describe the template/form and
 * deliberately discard anything personal:
 *
 *  - **Geometry** — aspect ratio / orientation / fill (ID-1 card vs A4 sheet).
 *  - **Layout** — an 8×8 ink-density heatmap (where the text/photo blocks sit).
 *  - **Colour** — a small HSV histogram (security tints, white invoices, …).
 *  - **Structure** — MRZ present? table gridlines? a photo block, and where?
 *  - **Template text** — the OCR text with personal values *redacted* to
 *    structure-preserving placeholders (`CPF <NUM>`, `VALIDADE <DATE>`). The
 *    remaining tokens are the printed template; the per-document corpus step
 *    then drops anything that doesn't recur (names are unique → removed).
 *
 * Everything here is a number or a redacted template token — never a raw image,
 * face, name, or document number. The same module runs in the offline harness
 * (to build the corpus) and, later, in the browser at inference time.
 */

/** Minimal structural view of pixels, satisfied by both `ImageData` and Node canvas. */
export interface PixelView {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/** A normalised bounding box (0..1), as produced by the detector. */
export interface FeatureBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Grid resolution for the layout heatmap (LAYOUT_N × LAYOUT_N cells). */
export const LAYOUT_N = 8;
/** HSV histogram bins: hue × saturation × value. */
const HUE_BINS = 6;
const SAT_BINS = 2;
const VAL_BINS = 2;
export const COLOR_BINS = HUE_BINS * SAT_BINS * VAL_BINS; // 24

/** The PII-free feature record for one document. */
export interface DocFeatures {
  /** width/height of the document in pixels (form factor). */
  aspect: number;
  orientation: 'portrait' | 'landscape' | 'square';
  /** document area as a fraction of the frame. */
  fill: number;
  /** LAYOUT_N² ink-density values in [0,1], row-major. */
  layout: number[];
  /** COLOR_BINS normalised HSV histogram. */
  color: number[];
  /** A machine-readable zone (`<<<`) was detected. */
  hasMrz: boolean;
  /** Fraction of rows spanned by a long horizontal line (table-ness). */
  tableness: number;
  /** A photo-like block was found, and its centre within the box (else -1). */
  photoPresent: boolean;
  photoX: number;
  photoY: number;
  /** Redacted template tokens (personal values already masked to placeholders). */
  tokens: string[];
}

/** Extract the full PII-free feature set for one document. */
export function extractFeatures(img: PixelView, box: FeatureBox | null, text: string): DocFeatures {
  const W = img.width;
  const H = img.height;
  const x0 = box ? Math.max(0, Math.round(box.x * W)) : 0;
  const y0 = box ? Math.max(0, Math.round(box.y * H)) : 0;
  const x1 = box ? Math.min(W, Math.round((box.x + box.w) * W)) : W;
  const y1 = box ? Math.min(H, Math.round((box.y + box.h) * H)) : H;
  const bw = Math.max(1, x1 - x0);
  const bh = Math.max(1, y1 - y0);

  const aspect = bw / bh;
  const orientation = aspect > 1.2 ? 'landscape' : aspect < 0.83 ? 'portrait' : 'square';
  const fill = box ? box.w * box.h : 1;

  // One pass over the box: grayscale (for layout/threshold), HSV (for colour).
  const gray = new Uint8Array(bw * bh);
  const color = new Float64Array(COLOR_BINS);
  const d = img.data;
  let satSum = 0;
  for (let y = y0, gy = 0; y < y1; y++, gy++) {
    for (let x = x0, gx = 0; x < x1; x++, gx++) {
      const p = (y * W + x) * 4;
      const r = d[p], g = d[p + 1], b = d[p + 2];
      gray[gy * bw + gx] = (r * 299 + g * 587 + b * 114) / 1000 | 0;
      const [h, s, v] = rgbToHsv(r, g, b);
      satSum += s;
      const hb = Math.min(HUE_BINS - 1, (h * HUE_BINS) | 0);
      const sb = Math.min(SAT_BINS - 1, (s * SAT_BINS) | 0);
      const vb = Math.min(VAL_BINS - 1, (v * VAL_BINS) | 0);
      color[(hb * SAT_BINS + sb) * VAL_BINS + vb]++;
    }
  }
  const area = bw * bh;
  for (let i = 0; i < color.length; i++) color[i] /= area;
  const meanSat = satSum / area;

  const t = otsu(gray);

  // Layout heatmap: per-cell ink (dark) density.
  const layout = new Array(LAYOUT_N * LAYOUT_N).fill(0);
  const cellCount = new Array(LAYOUT_N * LAYOUT_N).fill(0);
  for (let gy = 0; gy < bh; gy++) {
    const cy = Math.min(LAYOUT_N - 1, ((gy / bh) * LAYOUT_N) | 0);
    for (let gx = 0; gx < bw; gx++) {
      const cx = Math.min(LAYOUT_N - 1, ((gx / bw) * LAYOUT_N) | 0);
      const cell = cy * LAYOUT_N + cx;
      cellCount[cell]++;
      if (gray[gy * bw + gx] < t) layout[cell]++;
    }
  }
  for (let i = 0; i < layout.length; i++) layout[i] = cellCount[i] ? layout[i] / cellCount[i] : 0;

  const tableness = tablenessScore(gray, bw, bh, t);
  const photo = photoBlock(gray, bw, bh, t, meanSat, color);
  const hasMrz = detectMrz(text);
  const tokens = redactToTokens(text);

  return {
    aspect, orientation, fill, layout, color: Array.from(color),
    hasMrz, tableness,
    photoPresent: photo.present, photoX: photo.x, photoY: photo.y,
    tokens,
  };
}

// --- structural signals ------------------------------------------------------

/** Fraction of rows containing a long horizontal dark run (gridline → invoice). */
function tablenessScore(gray: Uint8Array, w: number, h: number, t: number): number {
  let lineRows = 0;
  const need = w * 0.5;
  for (let y = 0; y < h; y++) {
    let run = 0, best = 0;
    const base = y * w;
    for (let x = 0; x < w; x++) {
      if (gray[base + x] < t) {
        if (++run > best) best = run;
      } else {
        run = 0;
      }
    }
    if (best >= need) lineRows++;
  }
  return lineRows / h;
}

/**
 * Find a photo-like block: a 4×4 cell that is more saturated than the page and
 * carries little text (smooth colour, not print). Approximate — its *presence
 * and position* are the signal (ID with a face vs a text-only sheet).
 */
function photoBlock(
  gray: Uint8Array, w: number, h: number, t: number, meanSat: number, _color: Float64Array,
): { present: boolean; x: number; y: number } {
  const N = 4;
  let best = -1, bestX = 0, bestY = 0;
  for (let cy = 0; cy < N; cy++) {
    for (let cx = 0; cx < N; cx++) {
      const xa = ((cx * w) / N) | 0, xb = (((cx + 1) * w) / N) | 0;
      const ya = ((cy * h) / N) | 0, yb = (((cy + 1) * h) / N) | 0;
      let dark = 0, n = 0;
      for (let y = ya; y < yb; y++) {
        for (let x = xa; x < xb; x++, n++) if (gray[y * w + x] < t) dark++;
      }
      const ink = n ? dark / n : 0;
      // Smooth (low ink) cells score highest; saturated pages bias toward photos.
      const score = (1 - ink) * (0.5 + meanSat);
      if (ink < 0.25 && score > best) {
        best = score;
        bestX = (cx + 0.5) / N;
        bestY = (cy + 0.5) / N;
      }
    }
  }
  return best > 0 ? { present: true, x: bestX, y: bestY } : { present: false, x: -1, y: -1 };
}

function detectMrz(text: string): boolean {
  for (const line of text.split('\n')) {
    if ((line.match(/</g)?.length ?? 0) >= 8) return true;
  }
  return false;
}

// --- template text: redaction -----------------------------------------------

/**
 * Redact personal values to structure-preserving placeholders, then tokenise.
 * This removes structured PII (CPF, dates, numbers, emails, plates, MRZ) while
 * keeping the *shape* of the template. Free-text names that slip through are
 * removed later by the corpus document-frequency filter (a name is unique to
 * one document; template words recur), so the stored corpus stays PII-free.
 */
export function redactToTokens(text: string): string[] {
  let s = text
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, ' <EMAIL> ')
    .replace(/\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g, ' <CNPJ> ')
    .replace(/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, ' <CPF> ')
    .replace(/\b\d{5}-?\d{3}\b/g, ' <CEP> ')
    .replace(/\b\d{1,2}[/.\-]\d{1,2}[/.\-]\d{2,4}\b/g, ' <DATE> ')
    .replace(/\b[A-Za-z]{3}-?\d[A-Za-z0-9]\d{2}\b/g, ' <PLATE> ')
    .replace(/[A-Z0-9<]*<<[A-Z0-9<]*/g, ' <MRZ> ')
    .replace(/\b\d{3,}\b/g, ' <NUM> ');

  s = s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase();

  const out: string[] = [];
  for (const tok of s.split(/[^A-Z0-9<>]+/)) {
    if (tok.startsWith('<') && tok.endsWith('>')) out.push(tok);
    else if (tok.length >= 2 && /[A-Z]/.test(tok)) out.push(tok);
  }
  return out;
}

// --- small helpers -----------------------------------------------------------

/** RGB (0..255) → HSV, each component in [0,1). */
function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  if (delta > 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h /= 6;
    if (h < 0) h += 1;
  }
  const s = max === 0 ? 0 : delta / max;
  return [h, s, max];
}

/** Otsu threshold over a grayscale buffer. */
function otsu(gray: Uint8Array): number {
  const hist = new Uint32Array(256);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  const total = gray.length;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, best = 0, bestVar = -1;
  for (let i = 0; i < 256; i++) {
    wB += hist[i];
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += i * hist[i];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > bestVar) { bestVar = between; best = i; }
  }
  return best;
}
